/**
 * S14 export/import flow state. Lives OUTSIDE the toolbar kebab (Radix
 * unmounts dropdown content on close, so dialog state can't sit next to the
 * menu items) — DashboardTab mounts this hook once and hands the flow to
 * `DashboardExportMenuItems` (triggers) + `DashboardExportDialogs` (dialogs).
 */
import { useState } from 'react';
import { toast } from '@/components/ui/toaster';
import { useProRequiredHandler } from '@/core/licensing';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import type { Dashboard } from '../../types/dashboardTypes';
import { useDashboardStore } from '../../store/dashboardStore';
import {
  serializeDefinition,
  withNewIdentity,
  applyConnectionRemap,
  type ConnectionRef,
} from '../../services/definitionIo';
import {
  captureDashboardPdf,
  captureDashboardPng,
  exportDashboardDefinition,
  importDashboardDefinition,
  type ExportOutcome,
} from '../../services/dashboardExportService';
import { exportDashboardHtmlSnapshot } from '../../services/htmlSnapshotExportService';

export type DashboardExportFlow = {
  exportDefinition: () => void;
  exportPng: () => void;
  exportPdf: () => void;
  /** Opens the "Export as HTML…" dialog (S15) — the actual export runs on confirm. */
  exportHtmlSnapshot: () => void;
  htmlSnapshotDialogOpen: boolean;
  setHtmlSnapshotDialogOpen: (open: boolean) => void;
  confirmExportHtmlSnapshot: (includeQueries: boolean) => void;
  importDefinition: () => void;
  /** Parsed definition whose id already exists — awaiting "import as copy". */
  conflict: Dashboard | null;
  confirmImportAsCopy: () => void;
  cancelConflict: () => void;
  /** Freshly imported dashboard referencing connections this machine lacks. */
  remap: { dashboard: Dashboard; refs: ConnectionRef[] } | null;
  applyRemap: (mapping: Record<string, string>) => void;
  closeRemap: () => void;
};

const surfaceOutcome = (title: string) => (outcome: ExportOutcome) => {
  if (!outcome.ok && !outcome.cancelled) {
    toast.error({ title, description: outcome.error });
  }
};

export function useDashboardExportFlow(
  dashboard: Dashboard | null,
  onImported: (id: string) => void,
): DashboardExportFlow {
  const [conflict, setConflict] = useState<Dashboard | null>(null);
  const [remap, setRemap] = useState<{ dashboard: Dashboard; refs: ConnectionRef[] } | null>(null);
  const [htmlSnapshotDialogOpen, setHtmlSnapshotDialogOpen] = useState(false);
  const { handleProRequired } = useProRequiredHandler();

  const runImport = async (json?: string, isCopy = false) => {
    const known = new Set(useConnectionStore.getState().connections.map((c) => c.id));
    const result = await importDashboardDefinition(known, json ? { json } : undefined);
    if (result.kind === 'cancelled') return;
    if (result.kind === 'error') {
      toast.error({ title: 'Import failed', description: result.error });
      return;
    }
    if (result.kind === 'conflict') {
      setConflict(result.parsed);
      return;
    }
    window.electronAPI?.analytics
      ?.track('feature:dashboard_import', { copy: isCopy })
      .catch(() => {});
    if (result.missingRefs.length > 0) {
      const n = result.missingRefs.length;
      toast.warning({
        title: `Imported “${result.dashboard.name}” — ${n} ${n === 1 ? 'connection needs' : 'connections need'} remapping`,
        description: result.warning,
      });
    } else if (result.warning) {
      toast.warning({ title: `Imported “${result.dashboard.name}”`, description: result.warning });
    } else {
      toast.success({ title: `Imported “${result.dashboard.name}”` });
    }
    onImported(result.dashboard.id);
    if (result.missingRefs.length > 0) {
      setRemap({ dashboard: result.dashboard, refs: result.missingRefs });
    }
  };

  return {
    exportDefinition: () => {
      if (dashboard) void exportDashboardDefinition(dashboard).then(surfaceOutcome('Export failed'));
    },
    exportPng: () => {
      if (dashboard) void captureDashboardPng(dashboard).then(surfaceOutcome('PNG export failed'));
    },
    exportPdf: () => {
      if (dashboard) void captureDashboardPdf(dashboard).then(surfaceOutcome('PDF export failed'));
    },
    exportHtmlSnapshot: () => setHtmlSnapshotDialogOpen(true),
    htmlSnapshotDialogOpen,
    setHtmlSnapshotDialogOpen,
    confirmExportHtmlSnapshot: (includeQueries) => {
      setHtmlSnapshotDialogOpen(false);
      if (!dashboard) return;
      void exportDashboardHtmlSnapshot(dashboard, includeQueries).then((outcome) => {
        if (outcome.ok) return;
        if (outcome.proRequired) {
          handleProRequired(outcome.proRequired);
          return;
        }
        if (!outcome.cancelled) {
          toast.error({ title: 'HTML export failed', description: outcome.error });
        }
      });
    },
    importDefinition: () => void runImport(),
    conflict,
    confirmImportAsCopy: () => {
      if (!conflict) return;
      const copy = withNewIdentity(conflict, crypto.randomUUID());
      setConflict(null);
      void runImport(serializeDefinition(copy), true);
    },
    cancelConflict: () => setConflict(null),
    remap,
    applyRemap: (mapping) => {
      if (!remap) return;
      const remapped = applyConnectionRemap(remap.dashboard, mapping);
      setRemap(null);
      void useDashboardStore
        .getState()
        .save(remapped, remap.dashboard.revision)
        .then((result) => {
          if (!result.ok) {
            toast.error({ title: 'Remap failed', description: result.detail ?? result.error });
          }
        });
    },
    closeRemap: () => setRemap(null),
  };
}
